import { AlertTriangle, Heart, ArrowRight, PhoneCall } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const COPY = {
  de: {
    eyebrow: "Du bist hier richtig.",
    title: "In Krisen. In Beziehungen. Im Stillen.",
    body:
      "RED FLAG ist nicht nur ein Podcast — es ist eine Anlaufstelle. Wenn du Warnsignale erkennst, in einer toxischen Situation steckst oder einfach nicht mehr weiterweißt: hier findest du Klarheit und Wege weiter.",
    sat: "Schweizer Akademie für Transformation",
    satRole: "Beratung & Transformation",
    herz: "Herzbewegung",
    herzRole: "Community & Hilfe",
    crisis: "Akute Krise · 24/7",
    crisisDesc: "Bist du in unmittelbarer Gefahr? Wende dich sofort an die Dargebotene Hand (Tel. 143) oder im Notfall an 144.",
    ctaPrimary: "Hilfe finden",
    ctaSecondary: "Mehr erfahren",
  },
  en: {
    eyebrow: "You're in the right place.",
    title: "In crisis. In relationships. In silence.",
    body:
      "RED FLAG is more than a podcast — it's a point of contact. When you spot warning signs, are stuck in a toxic situation, or simply don't know how to move forward: this is where you find clarity and a way out.",
    sat: "Swiss Academy for Transformation",
    satRole: "Coaching & transformation",
    herz: "Herzbewegung",
    herzRole: "Community & support",
    crisis: "Acute crisis · 24/7",
    crisisDesc:
      "In immediate danger? Reach the Swiss helpline (143) immediately, or call 144 in a medical emergency.",
    ctaPrimary: "Find help",
    ctaSecondary: "Learn more",
  },
};

export default function CrisisBanner() {
  const { lang } = useLanguage();
  const t = COPY[lang];

  return (
    <section
      id="help"
      data-testid="crisis-banner"
      className="relative bg-[#0A0A0A] border-y-2 border-redflag py-10 md:py-12 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-r from-redflag/10 via-transparent to-redflag/10 pointer-events-none"
      />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 font-mono-plex text-[10px] uppercase tracking-widest text-redflag">
              <AlertTriangle size={14} className="live-dot" />
              {t.eyebrow}
            </div>
            <h2
              data-testid="crisis-headline"
              className="font-display uppercase mt-3 text-3xl md:text-4xl lg:text-5xl leading-[0.95] tracking-tight text-white"
            >
              {t.title}
            </h2>
            <p className="mt-4 text-base md:text-[17px] text-white/75 leading-relaxed max-w-2xl">
              {t.body}
            </p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a
                href="/hilfe"
                data-testid="crisis-cta-primary"
                className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm uppercase tracking-wide font-medium"
              >
                <AlertTriangle size={16} />
                {t.ctaPrimary}
                <ArrowRight size={16} />
              </a>
              <a
                href="#ecosystem"
                data-testid="crisis-cta-secondary"
                className="btn-ghost inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm uppercase tracking-wide font-medium"
              >
                {t.ctaSecondary}
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-redflag/30 bg-[#160708] p-5 md:p-6">
              <div className="font-mono-plex text-[10px] uppercase tracking-widest text-redflag flex items-center gap-2">
                <PhoneCall size={12} /> {t.crisis}
              </div>
              <p className="mt-2 text-sm text-white/75 leading-relaxed">
                {t.crisisDesc}
              </p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10">
                <div
                  data-testid="crisis-partner-sat"
                  className="bg-[#0A0A0A] p-4"
                >
                  <div className="font-mono-plex text-[10px] uppercase tracking-widest text-white/45">
                    {t.satRole}
                  </div>
                  <div className="mt-1 font-display uppercase text-sm leading-tight text-white">
                    {t.sat}
                  </div>
                  <div className="text-xs text-redflag mt-0.5">GmbH</div>
                </div>
                <div
                  data-testid="crisis-partner-herz"
                  className="bg-[#0A0A0A] p-4"
                >
                  <div className="font-mono-plex text-[10px] uppercase tracking-widest text-white/45 flex items-center gap-1.5">
                    <Heart size={10} className="text-redflag" /> {t.herzRole}
                  </div>
                  <div className="mt-1 font-display uppercase text-sm leading-tight text-white">
                    {t.herz}
                  </div>
                  <div className="text-xs text-white/40 mt-0.5">Community</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
